import CryptoJS from "crypto-js";

/**
 * Encryption Service for handling sensitive personal data in Member Frontend
 * Implements two-way encryption for categories B & C data as per security requirements
 * Must use same encryption key as admin backend for shared database compatibility
 */
class EncryptionService {
  constructor() {
    // Use environment variable for encryption key - must match admin backend
    this.encryptionKey =
      process.env.VUE_APP_ENCRYPTION_KEY ||
      "default-dev-key-change-in-production";

    // Field mappings for different encryption requirements
    this.encryptionConfig = {
      // Category A - One-way encryption (handled by bcrypt in password service)
      categoryA: {
        passwords: "hash", // Already implemented with bcrypt
        biometricData: "hash" // If implemented in future
      },

      // Category B & C - Two-way encryption required
      categoryB: {
        driverLicense: "encrypt",
        passport: "encrypt",
        foreignerRegistration: "encrypt",
        uniqueIdentification: "encrypt",
        accountNumbers: "encrypt",
        creditCardNumbers: "encrypt",
        locationData: "encrypt",
        accountKey: "encrypt" // Member account keys
      },

      categoryC: {
        email: "encrypt",
        name: "encrypt",
        firstname: "encrypt",
        lastname: "encrypt",
        address: "encrypt",
        phoneNumber: "encrypt",
        mobileNumber: "encrypt",
        mobile: "encrypt"
      }
    };
  }

  /**
   * Encrypt sensitive data using AES-256
   * @param {string} data - Data to encrypt
   * @returns {string} - Encrypted data
   */
  encrypt(data) {
    if (!data || typeof data !== "string") {
      return data;
    }

    try {
      // Standard CryptoJS encryption - matches Cloud Functions implementation
      const encrypted = CryptoJS.AES.encrypt(data, this.encryptionKey);
      return encrypted.toString();
    } catch (error) {
      console.error("Encryption error:", error);
      throw new Error("Failed to encrypt data");
    }
  }

  /**
   * Decrypt sensitive data with backward compatibility
   * @param {string} encryptedData - Encrypted data to decrypt
   * @returns {string} - Decrypted data
   */
  decrypt(encryptedData) {
    if (!encryptedData || typeof encryptedData !== "string") {
      return encryptedData;
    }

    try {
      // Standard CryptoJS decryption - matches Cloud Functions implementation
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      // Return decrypted data if successful, otherwise return original (might be plain text)
      return decrypted && decrypted.length > 0 ? decrypted : encryptedData;
    } catch (error) {
      console.error("Decryption error:", error);
      // If decryption fails, return original data (might be plain text legacy data)
      return encryptedData;
    }
  }

  /**
   * Encrypt an object's sensitive fields based on configuration
   * @param {Object} dataObject - Object containing data to encrypt
   * @param {Array} fieldsToEncrypt - Array of field names to encrypt
   * @returns {Object} - Object with encrypted fields
   */
  encryptObject(dataObject, fieldsToEncrypt = []) {
    if (!dataObject || typeof dataObject !== "object") {
      return dataObject;
    }

    const encrypted = { ...dataObject };

    fieldsToEncrypt.forEach(field => {
      if (encrypted[field] && typeof encrypted[field] === "string") {
        encrypted[field] = this.encrypt(encrypted[field]);
      }
    });

    return encrypted;
  }

  /**
   * Decrypt an object's sensitive fields
   * @param {Object} dataObject - Object containing encrypted data
   * @param {Array} fieldsToDecrypt - Array of field names to decrypt
   * @returns {Object} - Object with decrypted fields
   */
  decryptObject(dataObject, fieldsToDecrypt = []) {
    if (!dataObject || typeof dataObject !== "object") {
      return dataObject;
    }

    const decrypted = { ...dataObject };

    fieldsToDecrypt.forEach(field => {
      if (decrypted[field] && typeof decrypted[field] === "string") {
        try {
          decrypted[field] = this.decrypt(decrypted[field]);
        } catch (error) {
          // If decryption fails, field might not be encrypted
          console.warn(`Failed to decrypt field ${field}:`, error.message);
        }
      }
    });

    return decrypted;
  }

  /**
   * Get list of fields that require encryption for a given data type
   * @param {string} dataType - Type of data (member, admin, company, etc.)
   * @returns {Array} - Array of field names requiring encryption
   */
  getEncryptionFields(dataType) {
    const fieldMappings = {
      member: ["firstname", "lastname", "email", "mobile"],
      order: ["name", "email", "country", "postcode", "state", "suburb"],
      admin: ["name", "email", "phone", "mobile", "address"],
      company: [
        "contactName",
        "contactEmail",
        "contactPhone",
        "address",
        "accountNumbers"
      ],
      financial: ["accountNumbers", "creditCardNumbers", "bankDetails"],
      location: ["address", "coordinates", "locationData"]
    };

    return fieldMappings[dataType] || [];
  }

  /**
   * Check if data is encrypted by attempting to decrypt it
   * @param {string} data - Data to check
   * @returns {boolean} - True if data appears to be encrypted
   */
  isEncrypted(data) {
    if (!data || typeof data !== "string") {
      return false;
    }

    // If data is too short, it's likely not encrypted
    if (data.length < 20) {
      return false;
    }

    try {
      // Try to decrypt the data
      const bytes = CryptoJS.AES.decrypt(data, this.encryptionKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      // If we get a valid UTF-8 string back, it was likely encrypted
      // If decryption fails or returns empty, it was likely not encrypted
      return decrypted.length > 0;
    } catch (error) {
      // If decryption throws an error, data is likely not encrypted
      return false;
    }
  }

  /**
   * Safely decrypt data with backwards compatibility
   * @param {string} data - Data to decrypt (may or may not be encrypted)
   * @returns {string} - Decrypted data or original data if not encrypted
   */
  safeDecrypt(data) {
    if (!data || typeof data !== "string") {
      return data;
    }

    // Check if data is encrypted first
    if (this.isEncrypted(data)) {
      try {
        return this.decrypt(data);
      } catch (error) {
        console.warn(
          "Failed to decrypt data, returning original:",
          error.message
        );
        return data;
      }
    }

    // Return data as-is if not encrypted
    return data;
  }

  /**
   * Hash data using SHA-256 (for one-way encryption requirements)
   * @param {string} data - Data to hash
   * @returns {string} - SHA-256 hash
   */
  hash(data) {
    if (!data || typeof data !== "string") {
      return data;
    }

    try {
      return CryptoJS.SHA256(data).toString();
    } catch (error) {
      console.error("Hashing error:", error);
      throw new Error("Failed to hash data");
    }
  }

  /**
   * Validate the encryption configuration and key
   * @returns {Object} - Validation results
   */
  validateConfiguration() {
    const results = {
      isValid: true,
      warnings: [],
      errors: []
    };

    // Check if using default/development key in production
    if (process.env.NODE_ENV === "production") {
      if (
        this.encryptionKey === "default-dev-key-change-in-production" ||
        this.encryptionKey.includes("dev") ||
        this.encryptionKey.includes("test")
      ) {
        results.errors.push("Using development encryption key in production");
        results.isValid = false;
      }
    }

    return results;
  }

  /**
   * Generate a new encryption key (for development/setup purposes)
   * @returns {string} - New encryption key
   */
  static generateEncryptionKey() {
    return CryptoJS.lib.WordArray.random(256 / 8).toString();
  }
}

// Export singleton instance
export default new EncryptionService();
